import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Injector,
  Input,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { debounceTime, merge } from 'rxjs';

import type { CurrentUser } from '@core/account-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  IconComponent,
  SelectController,
  IconButtonComponent,
  TruncateComponent,
  AvatarComponent,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import type { MailboxSelectItem } from '../mailbox-select.types';
import { MailboxSelectType } from '../mailbox-select.types';

@Component({
  selector: 'rt-mailbox-select-trigger',
  styleUrls: ['./mailbox-select-trigger.component.less'],
  templateUrl: './mailbox-select-trigger.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    IconComponent,
    IconButtonComponent,
    TruncateComponent,
    AvatarComponent,
    NgTemplateOutlet,
    TranslatePipe,
  ],
  host: {
    '[class.minimized]': 'minimized',
  },
})
export class MailboxSelectTriggerButtonComponent {
  public readonly selectCtrl = inject<
    SelectController<{
      items: MailboxSelectItem[];
    }>
  >(SelectController);
  public readonly cdRef = inject(ChangeDetectorRef);
  public readonly MailboxSelectType = MailboxSelectType;
  public readonly i18n = inject(I18nService);

  @Input() public minimized: boolean = false;
  @Input() public triggerSize: 'small' | 'medium' = 'medium';
  @Input() public disabled?: boolean;
  @Input({ required: true }) public currentUser: CurrentUser;

  private untilDestroyed = injectUntilDestroyed();
  private injector = inject(Injector);

  public ngOnInit() {
    merge(
      toObservable(this.selectCtrl.itemSelector.selected, { injector: this.injector }),
      this.selectCtrl.detectChanges$,
    )
      .pipe(debounceTime(0), this.untilDestroyed)
      .subscribe(() => this.cdRef.detectChanges());
  }
}
